package vegasful.admin.views

import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import vegasful.admin.Application
import vegasful.admin.api.ThingStatus
import vegasful.admin.api.ThingUpdateInput
import vegasful.admin.api.client.AdminThingLayer
import vegasful.admin.api.client.Venue
import vegasful.admin.api.client._AdminThingLayerQuery
import vegasful.admin.components.*

/**
 * The city root. Every thing to do must sit at a venue — that link *is* the whole-catalogue query,
 * so a venue-less record would be invisible to every listing rather than merely unplaced. Anything
 * with no more specific home goes here.
 */
private const val ROOT_VENUE_ID = "las-vegas"

private fun _AdminThingLayerQuery.thingLayerQuery() {
    this.name
    this.description
    this.tagline
    this.summary
    this.aliases
    this.hours
    this.hoursSources
    this.typicalDuration
    this.priceFrom
    this.ticketsUrl
    this.status
    this.rankingScore
    this.tags {
        this.id
        this.name
    }
    this.images {
        this.id
        this.height
        this.width
        this.uri
        this.withWidth(256) {
            this.uri
        }
    }
    this.venue {
        this.id
        this.name
        this.path
    }
    this.links {
        this.type
        this.locator
    }
}

/**
 * The editor for a single thing to do, and the create form when [thingId] is null.
 *
 * Shaped after [venue]: one box of scalar fields with an explicit save, and the collection-shaped
 * fields (tags, images, links, aliases) in their own boxes that write immediately.
 */
@Composable
fun thingToDo(thingId: String?) {
    var local by remember { mutableStateOf<AdminThingLayer?>(null) }
    var availability by remember { mutableStateOf<String?>(null) }
    var openNow by remember { mutableStateOf<Boolean?>(null) }
    var loadError by remember { mutableStateOf<String?>(null) }

    suspend fun reload() {
        Application.mutate {
            admin {
                thingsToDo {
                    thing(thingId!!) {
                        this.id
                        local {
                            thingLayerQuery()
                        }
                        // The server-computed availability line. Reading it back is how an operator
                        // confirms the hours were understood the way they were meant — particularly
                        // for a past-midnight close, where the stored value is a duration.
                        resolved {
                            this.availability
                            this.openNow
                        }
                    }
                }
            }
        }.admin.thingsToDo.thing.let {
            local = it.local
            availability = it.resolved.availability
            openNow = it.resolved.openNow
        }
    }

    /**
     * Apply an update to the local layer. Every box on this page routes through here, so the
     * refreshed layer is what the next render reads.
     */
    suspend fun updateThing(update: ThingUpdateInput) {
        if (thingId == null) return
        Application.mutate {
            admin {
                thingsToDo {
                    thing(thingId) {
                        local {
                            update(update) {
                                thingLayerQuery()
                            }
                        }
                    }
                }
            }
        }.admin.thingsToDo.thing.local.update.let {
            local = it
        }
        // The resolved record is rebuilt by the pipeline the base write triggers, so re-read it
        // rather than assuming this update produced it.
        runCatching { reload() }
    }

    LaunchedEffect(thingId) {
        local = null
        availability = null
        openNow = null
        loadError = null
        if (thingId != null) {
            try {
                reload()
            } catch (t: Throwable) {
                loadError = t.message ?: "This thing to do could not be loaded."
            }
        }
    }

    contentContainer {
        entityView {
            title = if (thingId == null) "New Thing to Do" else (local?.name ?: thingId)
            breadcrumbs {
                crumb("Things to Do", "content/things")
            }

            if (thingId != null) {
                action {
                    this.title = "View on Site"
                    this.link = null
                    this.action = {
                        // Opened rather than navigated: the public page is a different app.
                        kotlinx.browser.window.open(
                            "https://${Application.domain}/things-to-do/$thingId",
                            "_blank"
                        )
                    }
                }
            }

            content {
                loadError?.let {
                    box({ title = "Error" }) { boxMessage(it) }
                }

                if (thingId == null) {
                    createThingForm()
                } else {
                    thingFieldsBox(thingId, local, availability, openNow) { updateThing(it) }

                    thingVenueBox(local?.venue) { venueId ->
                        updateThing(ThingUpdateInput(venue = venueId))
                    }

                    hoursBox(local?.hours) { hoursJson ->
                        updateThing(ThingUpdateInput(hours = hoursJson))
                    }

                    hoursSourcesBox(local?.hoursSources) { sourcesJson ->
                        updateThing(ThingUpdateInput(hoursSources = sourcesJson))
                    }

                    linksBox(local?.links.orEmpty()) {
                        updateThing(ThingUpdateInput(links = it))
                    }

                    imagesBox(local?.images.orEmpty().map { ImageBoxImage(it, true) }) { image, operation ->
                        when (operation) {
                            ImageBoxOperation.ADD -> updateThing(
                                ThingUpdateInput(images = (local?.images.orEmpty() + image).map { it.id })
                            )

                            ImageBoxOperation.REMOVE -> updateThing(
                                ThingUpdateInput(images = local?.images.orEmpty().map { it.id } - image.id)
                            )

                            ImageBoxOperation.EXCLUDE -> {}
                            ImageBoxOperation.ENABLE -> {}
                        }
                    }

                    tagsBox(local?.tags.orEmpty().map { TagBoxTag(it) }) { tag, operation ->
                        when (operation) {
                            TagOperation.ADD -> updateThing(
                                ThingUpdateInput(
                                    tags = (local?.tags.orEmpty().map { it.id } + tag.id).distinct()
                                )
                            )

                            TagOperation.REMOVE -> updateThing(
                                ThingUpdateInput(
                                    tags = (local?.tags.orEmpty().map { it.id } - tag.id).distinct()
                                )
                            )

                            else -> {}
                        }
                    }

                    thingAliasesBox(local?.aliases.orEmpty()) { aliases ->
                        updateThing(ThingUpdateInput(aliases = aliases))
                    }

                    thingStatusBox(thingId, local) { updateThing(it) }
                }
            }
        }
    }
}

/**
 * The scalar fields.
 *
 * Each field's displayed value is held as a plain string and diffed against the record on save.
 * That is deliberate: the update-input convention treats an absent field as "leave unchanged" and a
 * **blank string** as "clear", so emptying a box has to send `""`. Carrying the edit as a nullable
 * "changed value" instead would send `null` for a cleared box, which the server reads as no change —
 * and the field would silently refuse to clear.
 */
@Composable
private fun thingFieldsBox(
    thingId: String,
    local: AdminThingLayer?,
    availability: String?,
    openNow: Boolean?,
    update: suspend (ThingUpdateInput) -> Unit,
) {
    var name by remember(local) { mutableStateOf(local?.name.orEmpty()) }
    var tagline by remember(local) { mutableStateOf(local?.tagline.orEmpty()) }
    var summary by remember(local) { mutableStateOf(local?.summary.orEmpty()) }
    var description by remember(local) { mutableStateOf(local?.description.orEmpty()) }
    var ticketsUrl by remember(local) { mutableStateOf(local?.ticketsUrl.orEmpty()) }
    var typicalDuration by remember(local) { mutableStateOf(local?.typicalDuration?.toString().orEmpty()) }
    var priceFrom by remember(local) { mutableStateOf(local?.priceFrom?.toString().orEmpty()) }
    var rankingScore by remember(local) { mutableStateOf(local?.rankingScore?.toString().orEmpty()) }
    var error by remember(local) { mutableStateOf<String?>(null) }

    fun textChange(entered: String, stored: String?): String? =
        entered.takeIf { it != stored.orEmpty() }

    /**
     * Numbers are cleared with an out-of-range sentinel rather than a blank, because the input is
     * typed `Int` and the server clears these by rejecting the value it is given:
     * `typicalDuration` keeps only a value > 0, `priceFrom` only >= 0, `rankingScore` only 0..100.
     */
    fun numberChange(entered: String, stored: Int?, clearWith: Int): Int? {
        val parsed = entered.trim().toIntOrNull()
        return when {
            entered.isBlank() && stored == null -> null
            entered.isBlank() -> clearWith
            parsed == null -> null
            parsed == stored -> null
            else -> parsed
        }
    }

    val dirty = name != local?.name.orEmpty() ||
            tagline != local?.tagline.orEmpty() ||
            summary != local?.summary.orEmpty() ||
            description != local?.description.orEmpty() ||
            ticketsUrl != local?.ticketsUrl.orEmpty() ||
            typicalDuration != local?.typicalDuration?.toString().orEmpty() ||
            priceFrom != local?.priceFrom?.toString().orEmpty() ||
            rankingScore != local?.rankingScore?.toString().orEmpty()

    box({
        title = "Details"

        action {
            title = "Save"
            primary = true
            showProgressOnAction = true
            enabled = dirty
            action {
                error = null
                if (name.isBlank()) {
                    error = "A name is required."
                    return@action
                }
                try {
                    update(
                        ThingUpdateInput(
                            name = textChange(name, local?.name),
                            tagline = textChange(tagline, local?.tagline),
                            summary = textChange(summary, local?.summary),
                            description = textChange(description, local?.description),
                            ticketsUrl = textChange(ticketsUrl, local?.ticketsUrl),
                            typicalDuration = numberChange(typicalDuration, local?.typicalDuration, clearWith = 0),
                            priceFrom = numberChange(priceFrom, local?.priceFrom, clearWith = -1),
                            rankingScore = numberChange(rankingScore, local?.rankingScore, clearWith = -1),
                        )
                    )
                } catch (t: Throwable) {
                    error = t.message ?: "The thing to do could not be saved."
                }
            }
        }
    }) {
        error?.let { boxMessage(it) }

        dialogField("ID") {
            Input(InputType.Text) {
                disabled()
                value(thingId)
            }
        }

        dialogField("Name") {
            simpleTextField(name.ifBlank { null }) { name = it.orEmpty() }
        }

        dialogField("Tagline") {
            simpleTextField(tagline.ifBlank { null }) { tagline = it.orEmpty() }
        }

        dialogField("Summary", "Two or three sentences, plain text, used on cards.") {
            TextArea {
                style { height(60.px) }
                value(summary)
                onInput { summary = it.value }
            }
        }

        dialogField("Description") {
            richTextEditor(description) { description = it.orEmpty() }
        }

        sideBySideFields {
            dialogField("Typical Duration", "Minutes a visit takes. Blank clears it.") {
                simpleTextField(typicalDuration.ifBlank { null }) { typicalDuration = it.orEmpty() }
            }
            dialogField("Price From", "Lowest ticket price in whole dollars. Blank clears it.") {
                simpleTextField(priceFrom.ifBlank { null }) { priceFrom = it.orEmpty() }
            }
        }

        sideBySideFields {
            dialogField("Ranking Score", "0–100. 30 is ordinary.") {
                simpleTextField(rankingScore.ifBlank { null }) { rankingScore = it.orEmpty() }
            }
            dialogField("Tickets URL") {
                simpleTextField(ticketsUrl.ifBlank { null }) { ticketsUrl = it.orEmpty() }
            }
        }

        // Read-back of what the server makes of the stored hours.
        Div({
            style {
                marginTop(10.px)
                paddingTop(8.px)
                fontSize(0.85.cssRem)
                border(0.px, LineStyle.Solid, vegasful.admin.Theme.defaultDivider.value())
                borderWidth(1.px, 0.px, 0.px, 0.px)
            }
        }) {
            Div({ style { opacity(.7) } }) {
                Text("Computed from the saved hours (rebuilt just after a save, so it can lag a moment):")
            }
            Div {
                Text(
                    buildString {
                        append("Availability: ")
                        append(availability ?: "none — no hours recorded")
                        openNow?.let { append(if (it) " · open now" else " · closed now") }
                    }
                )
            }
        }
    }
}

/**
 * The venue. Never cleared: a blank falls back to the city root server-side, and this box makes that
 * explicit rather than offering a delete that would not do what it appears to.
 */
@Composable
private fun thingVenueBox(venue: Venue?, update: suspend (String) -> Unit) {
    box({
        title = "Venue"
    }) {
        Div({ style { fontSize(0.8.cssRem); opacity(.7); marginBottom(8.px) } }) {
            Text(
                "Every thing to do sits at a venue — the catalogue is a link query at the venue " +
                        "tree, so a record with no venue is invisible to every listing. Anything with no " +
                        "more specific home belongs at the $ROOT_VENUE_ID city root."
            )
        }

        dialogField("Current") {
            venue?.let {
                A(href = "#content/venues/${it.id}") {
                    Text("${it.name} (${it.path})")
                }
            } ?: Text("none — this record is invisible to listings until a venue is set")
        }

        dialogField("Change to") {
            venuesSearch({ true }, "Venue Search:") { selected ->
                update(selected.id)
            }
        }

        if (venue?.id != ROOT_VENUE_ID) {
            Div({ style { marginTop(8.px) } }) {
                button("Move to $ROOT_VENUE_ID root") {
                    update(ROOT_VENUE_ID)
                }
            }
        }
    }
}

@Composable
private fun thingAliasesBox(aliases: List<String>, update: suspend (List<String>) -> Unit) {
    var adding: String? by remember { mutableStateOf(null) }

    box({
        title = "Aliases"
        paddedContent = false

        action("Add", true) {
            adding = ""
        }
    }) {
        if (aliases.isEmpty() && adding == null) {
            boxMessage("This thing to do has no aliases.")
        } else {
            if (adding != null) {
                Div({ style { padding(1.cssRem) } }) {
                    dialogField("Add Alias") {
                        simpleTextField(adding) { adding = it ?: "" }
                    }
                    button {
                        title = "Save"
                        primary = true
                        action {
                            adding?.takeIf { it.isNotBlank() }?.let {
                                update(aliases + it)
                                adding = null
                            }
                        }
                    }
                    button {
                        title = "Cancel"
                        action { adding = null }
                    }
                }
            }

            if (aliases.isNotEmpty()) {
                table<String> {
                    items(aliases)
                    displayHeader = false

                    column {
                        content { Text(it) }
                    }

                    column {
                        width = 100.px
                        content { alias ->
                            icon("delete") {
                                action { update(aliases - alias) }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun thingStatusBox(
    thingId: String,
    local: AdminThingLayer?,
    update: suspend (ThingUpdateInput) -> Unit,
) {
    var status by remember(local) { mutableStateOf<ThingStatus?>(null) }
    var confirmingDelete by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    box({
        title = "Status"

        action {
            title = "Save"
            primary = true
            showProgressOnAction = true
            enabled = status != null && status != local?.status
            action {
                error = null
                try {
                    status?.let { update(ThingUpdateInput(status = it)) }
                } catch (t: Throwable) {
                    error = t.message ?: "The status could not be saved."
                }
            }
        }
    }) {
        error?.let { boxMessage(it) }

        dialogField("Status") {
            Select({
                onChange { event ->
                    status = event.value?.let { ThingStatus.valueOf(it) }
                }
            }) {
                val current = status ?: local?.status ?: ThingStatus.OPEN
                ThingStatus.entries.forEach { option ->
                    Option(option.name, { if (current == option) selected() }) {
                        Text(option.name.lowercase().replace("_", " "))
                    }
                }
            }
        }

        Div({
            style {
                marginTop(14.px)
                paddingTop(10.px)
                border(0.px, LineStyle.Solid, vegasful.admin.Theme.defaultDivider.value())
                borderWidth(1.px, 0.px, 0.px, 0.px)
            }
        }) {
            if (!confirmingDelete) {
                button("Delete this thing to do") { confirmingDelete = true }
            } else {
                Div({ style { marginBottom(8.px); fontSize(0.85.cssRem) } }) {
                    Text(
                        "Deleting removes the record and its links. Prefer setting the status to " +
                                "closed permanently if it merely no longer operates."
                    )
                }
                button {
                    title = "Delete permanently"
                    primary = true
                    showProgressOnAction = true
                    action {
                        error = null
                        try {
                            Application.mutate {
                                admin {
                                    thingsToDo {
                                        thing(thingId) {
                                            this.delete
                                        }
                                    }
                                }
                            }
                            Application.navigation.navigate("content/things")
                        } catch (t: Throwable) {
                            error = t.message ?: "The thing to do could not be deleted."
                        }
                    }
                }
                button("Cancel") { confirmingDelete = false }
            }
        }
    }
}

/**
 * The create form.
 *
 * A venue is mandatory here rather than optional-with-a-server-default, because the consequence of
 * omitting one is invisibility rather than a visible error. The field is pre-set to the city root so
 * that the common case is one keystroke, and the form refuses to submit without one.
 */
@Composable
private fun createThingForm() {
    var id by remember { mutableStateOf("") }
    var name by remember { mutableStateOf("") }
    var venueId by remember { mutableStateOf(ROOT_VENUE_ID) }
    var venueLabel by remember { mutableStateOf("$ROOT_VENUE_ID (city root)") }
    var error by remember { mutableStateOf<String?>(null) }

    box({
        title = "New Thing to Do"
    }) {
        error?.let { boxMessage(it) }

        dialogField("ID", "The public path is /things-to-do/<id>. Lower case, hyphenated.") {
            simpleTextField(id.ifBlank { null }) { id = it.orEmpty() }
        }

        dialogField("Name") {
            simpleTextField(name.ifBlank { null }) { name = it.orEmpty() }
        }

        dialogField("Venue", "Required. Anything with no more specific home sits at the city root.") {
            Div {
                Div({ style { marginBottom(6.px) } }) {
                    Text("Selected: $venueLabel")
                }
                venuesSearch({ true }, "Venue Search:") { selected ->
                    venueId = selected.id
                    venueLabel = "${selected.name} (${selected.path})"
                }
                if (venueId != ROOT_VENUE_ID) {
                    Div({ style { marginTop(6.px) } }) {
                        button("Use the $ROOT_VENUE_ID city root") {
                            venueId = ROOT_VENUE_ID
                            venueLabel = "$ROOT_VENUE_ID (city root)"
                        }
                    }
                }
            }
        }

        Div({ style { marginTop(10.px) } }) {
            button {
                title = "Create"
                primary = true
                showProgressOnAction = true
                action {
                    error = null
                    when {
                        id.isBlank() -> error = "An ID is required."
                        name.isBlank() -> error = "A name is required."
                        venueId.isBlank() -> error = "A venue is required."
                        else -> {
                            try {
                                Application.mutate {
                                    admin {
                                        thingsToDo {
                                            add(
                                                id.trim(),
                                                ThingUpdateInput(name = name.trim(), venue = venueId)
                                            ) {
                                                this.id
                                            }
                                        }
                                    }
                                }
                                Application.navigation.navigate("content/things/${id.trim()}")
                            } catch (t: Throwable) {
                                error = t.message ?: "The thing to do could not be created."
                            }
                        }
                    }
                }
            }
        }
    }
}
